import { useEffect, useRef, useState } from 'react'
import type Hls from 'hls.js'

/**
 * Canonical hero media used by the public landing page and the first-run
 * onboarding. Keeping the sources here prevents the two entry surfaces from
 * slowly drifting into different videos or loading strategies.
 */
export const TSCHEPPA_HERO_MEDIA = {
  landscapeHlsUrl: 'https://vod.arkturian.com/media/arkturian/u2_20260223_201355_1c29c823_transcoded/master.m3u8',
  portraitHlsUrl: 'https://vod.arkturian.com/media/arkturian/u2_20260223_201405_2e0dc25c_transcoded/master.m3u8',
  posterUrl: 'https://api-storage.arkturian.com/storage/media/102936?variant=medium&format=jpg',
} as const

interface TscheppaHeroVideoProps {
  className?: string
  paused?: boolean
  /**
   * Keep the poster as the entire first paint and only attach the decoder on
   * the following rendered frame. The onboarding uses this stricter mode so
   * adding motion cannot regress its FCP/LCP path.
   */
  deferUntilPaint?: boolean
}

/**
 * Poster-first responsive HLS hero.
 *
 * Native HLS is used on Safari/iOS. Other browsers load hls.js dynamically,
 * so the ~500 KB decoder never enters the initial bundle. The video remains
 * transparent until a frame has decoded; on every error the canonical poster
 * therefore stays visible without a black flash.
 */
export default function TscheppaHeroVideo({
  className,
  paused = false,
  deferUntilPaint = false,
}: TscheppaHeroVideoProps) {
  const posterRef = useRef<HTMLImageElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const pausedRef = useRef(paused)
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768)
  const [posterSettled, setPosterSettled] = useState(false)
  const [sourceReady, setSourceReady] = useState(() => !deferUntilPaint)
  const [frameReady, setFrameReady] = useState(false)

  useEffect(() => { pausedRef.current = paused }, [paused])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    if (paused) video.pause()
    else if (sourceReady) void video.play().catch(() => {})
  }, [paused, sourceReady])

  useEffect(() => {
    if (!deferUntilPaint || !posterSettled) return
    let secondFrame = 0
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => setSourceReady(true))
    })
    return () => {
      cancelAnimationFrame(firstFrame)
      if (secondFrame) cancelAnimationFrame(secondFrame)
    }
  }, [deferUntilPaint, posterSettled])

  useEffect(() => {
    if (!deferUntilPaint) return
    // React normally delivers load/error even for a cached image. The
    // synchronous complete check closes the remaining cache race without
    // starting HLS before the poster has settled.
    if (posterRef.current?.complete) setPosterSettled(true)
  }, [deferUntilPaint])

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    const video = videoRef.current
    if (!video || !sourceReady) return

    const src = isMobile
      ? TSCHEPPA_HERO_MEDIA.portraitHlsUrl
      : TSCHEPPA_HERO_MEDIA.landscapeHlsUrl
    let hls: Hls | null = null
    let cancelled = false
    setFrameReady(false)
    video.muted = true
    video.setAttribute('muted', '')

    const playWhenAllowed = () => {
      if (!pausedRef.current) void video.play().catch(() => {})
    }

    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src
      playWhenAllowed()
    } else {
      void import('hls.js').then(({ default: HlsCtor }) => {
        if (cancelled || !HlsCtor.isSupported()) return
        hls = new HlsCtor({ startLevel: -1 })
        hls.loadSource(src)
        hls.attachMedia(video)
        hls.on(HlsCtor.Events.MANIFEST_PARSED, playWhenAllowed)
      })
    }

    return () => {
      cancelled = true
      hls?.destroy()
      video.pause()
      video.removeAttribute('src')
      video.load()
    }
  }, [isMobile, sourceReady])

  const mediaStyle = {
    position: 'absolute' as const,
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
  }

  return (
    <div
      className={className}
      data-hero-state={frameReady ? 'video' : sourceReady ? 'loading' : 'poster'}
      aria-hidden="true"
    >
      <img
        ref={posterRef}
        className="tscheppa-hero-media__poster"
        src={TSCHEPPA_HERO_MEDIA.posterUrl}
        alt=""
        loading="eager"
        decoding="async"
        fetchPriority="high"
        onLoad={() => setPosterSettled(true)}
        onError={() => setPosterSettled(true)}
        style={{ ...mediaStyle, opacity: frameReady ? 0 : 1 }}
      />
      <video
        ref={videoRef}
        className="tscheppa-hero-media__video"
        muted
        loop
        playsInline
        preload="none"
        poster={TSCHEPPA_HERO_MEDIA.posterUrl}
        onLoadedData={() => setFrameReady(true)}
        onError={() => setFrameReady(false)}
        style={{
          ...mediaStyle,
          opacity: frameReady ? 1 : 0,
          transition: 'opacity 280ms ease',
        }}
      />
    </div>
  )
}
